import { supportingArchiveFolder } from "components/Documents/supportingMetadata";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { AxiosInstance } from "axios";
import { DocumentData } from "types";
import { SpecialUploadType } from "components/Documents/types";
import { supportingFilename } from "components/Documents/supportingDocumentFiles";
import { requestDownload } from "pages/Project/hooks/useCreateProject";

const translations: Record<string, string> = {
  external: "Extern",
  internal: "Intern",
  "conformity.pdf": "Konformitätserklärung.pdf",
  "installationConformity.pdf": "Einbauerklärung.pdf",
  "risk.pdf": "Risikobeurteilung.pdf",
  "plate.pdf": "Typenschild.pdf",
  "preview_manual.pdf": "Betriebsanleitung_Vorschau.pdf",
  "exitConformity.pdf": "Ausschluss.pdf",
};
const safePath = (name: string) =>
  name
    .replace(/\\/g, "/")
    .split("/")
    .filter((part) => part && part !== "." && part !== "..")
    .map((part) => part.replace(/[\u0000-\u001f\u007f]/g, ""))
    .join("/") || "document";
export const archiveEntries = (documents: DocumentData[]) => {
  const identities = new Set<string>();
  const paths = new Set<string>();
  return documents.flatMap((doc) => {
    const identity = doc.key || doc.name;
    if (identities.has(identity)) return [];
    identities.add(identity);
    const base =
      doc.type === SpecialUploadType.supportingDocument
        ? `${supportingArchiveFolder(doc)}/${safePath(supportingFilename(doc))}`
        : safePath(doc.name)
            .split("/")
            .map((p) => translations[p] || p)
            .join("/");
    let path = base;
    let suffix = 2;
    const dot =
      base.lastIndexOf(".") > base.lastIndexOf("/") + 1
        ? base.lastIndexOf(".")
        : base.length;
    while (paths.has(path))
      path = `${base.slice(0, dot)} (${suffix++})${base.slice(dot)}`;
    paths.add(path);
    return [{ doc, path }];
  });
};
export const fetchDocumentBytes = async (
  doc: DocumentData,
  projectId: string,
  client?: AxiosInstance,
) => {
  const key =
    doc.key ||
    (doc.type === SpecialUploadType.supportingDocument ? doc.name : undefined);
  const url =
    key && client
      ? await requestDownload(client, key, projectId)
      : doc.location;
  if (!url) throw new Error("Document has no download URL");
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Download failed (${response.status})`);
  return response.arrayBuffer();
};
export const zipDownload = async (
  documents: DocumentData[],
  projectId: string,
  projectName: string,
  client?: AxiosInstance,
) => {
  const zip = new JSZip();
  const folder = zip.folder(
    `Technische Dokumentation ${safePath(projectName).replace(/\//g, "_")}`,
  )!;
  for (const { doc, path } of archiveEntries(documents)) {
    folder.file(path, await fetchDocumentBytes(doc, projectId, client));
  }
  const content = await zip.generateAsync({ type: "blob" });
  saveAs(
    content,
    `technische_dokumentation_${projectName.replace(/[\\/]/g, "_")}.zip`,
  );
};
